/**
 * Collapse a concrete path to a stable, id-free surface key.
 *
 *   /runs/8f3c-…      →  runs/:id
 *   /connections/new  →  connections/new
 *   /admin/users      →  admin/users
 *   /                 →  home
 *
 * This is the client-side half of a telemetry boundary: it is what stops
 * `/runs/<run id>` becoming a per-run dimension in Application Insights. There
 * is exactly ONE definition of what a surface is, because a second copy would
 * eventually disagree with this one and the copy that disagreed would be the
 * one leaking ids.
 *
 * Sent as `x-surface` on every request (API-STANDARD.md §4 companion), so a
 * server-side failure can be described in product terms ("the run detail
 * screen") rather than in route terms.
 */

/** Segments that are ids rather than route words, collapsed to `:id`. */
function looksLikeId(segment: string): boolean {
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return true;
  if (/^\d+$/.test(segment)) return true;
  // Opaque tokens: long and not a word anyone would name a route.
  return segment.length >= 16 && !/^[a-z][a-z-]*$/.test(segment);
}

export function toSurfaceKey(pathname: string): string {
  const all = pathname.split("/").filter(Boolean);
  // The instance prefix (`/i/<id>`, round 9 T-58) is not part of the screen: `/i/<id>/runs/<id>`
  // is the same surface as `/runs/<id>` was, so the dimension keeps its values.
  const segments =
    all[0] === "i" && all[1] !== undefined && looksLikeId(all[1]) ? all.slice(2) : all;
  if (segments.length === 0) return "home";
  return segments.map((s) => (looksLikeId(s) ? ":id" : s)).join("/");
}

/**
 * The surface the browser is on RIGHT NOW, for tagging an outbound request.
 *
 * Reads `window.location` rather than the router's state, so it works from the
 * fetch layer with no React coupling and cannot go stale mid-flight. Returns
 * undefined outside a browser so callers can spread it without a guard.
 */
export function currentSurfaceKey(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return toSurfaceKey(window.location.pathname);
  } catch {
    return undefined;
  }
}
